import { UserElement } from "../types";
import { Usedbo } from "../../../hooks/useDBO.ts";
import useCTX from "../../../hooks/useCTX.ts";
import SelectFromList from "../../../components/SelectFromList.jsx";
import Dialog from "../../../components/Dialog.jsx";
import { ArrowRightLeft } from "lucide-react";
import { useState } from "react";

/**
 * ChangeManager — Dialog for reassigning a staff member's manager.
 * Uses custom Dialog.jsx (no MUI).
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 2.0
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog visibility
 * @param {function} props.setOpen - Toggle dialog
 * @param {UserElement} props.user - Staff member being reassigned
 * @param {Usedbo<UserElement>} props.myStaff - Staff collection to refresh
 * @returns {React.ReactElement}
 */
type DialogProps = {
  open: boolean;
  setOpen: (flag: boolean) => void;
  user: UserElement | undefined;
  myStaff: Usedbo<UserElement>;
};

export default function ChangeManager({ open, setOpen, user, myStaff }: DialogProps) {
  const { getStaffRole } = useCTX();
  const managers = getStaffRole("Manager");
  const [manager, setManager] = useState(user?.reportsTo || "");

  const handleConfirm = () => {
    myStaff.refresh();
    setOpen(false);
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      onConfirm={handleConfirm}
      title={`Change manager for ${user.name}`}
      titleIcon={<ArrowRightLeft size={18} />}
      confirmLabel="Go"
      size="sm"
    >
      <div className="flex flex-col gap-3">
        <label className="text-xs pl-1 text-slate-500">New Manager</label>
        <SelectFromList
          choices={managers.map((m: any) => ({ _id: m._id, name: m.name }))}
          value={manager}
          setValue={setManager}
          url={`/user/${user._id}`}
          field="reportsTo"
          fullWidth
        />
      </div>
    </Dialog>
  );
}
